/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * Convert uploaded raster images to WebP before they are stored or sent.
 */

export const WEBP_MIME = "image/webp";
export const WEBP_QUALITY = 0.82;

export function toWebpFileName(name = "image") {
  const trimmed = String(name || "image").trim() || "image";
  const base = trimmed.replace(/\.[^./\\]+$/, "") || "image";
  return `${base}.webp`;
}

export function isWebpFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const fileName = String(file.name || "").toLowerCase();
  return type === WEBP_MIME || fileName.endsWith(".webp");
}

function scaleSize(width, height, maxWidth, maxHeight) {
  const scale = Math.min(1, maxWidth / Math.max(width, 1), maxHeight / Math.max(height, 1));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function loadImageElement(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas encoding failed"));
    }, type, quality);
  });
}

function asFile(blob, fileName, type) {
  return new File([blob], fileName, { type, lastModified: Date.now() });
}

/**
 * Convert a File/Blob image to a .webp File.
 * Non-image values are returned unchanged. If the browser cannot encode WebP,
 * the original file is returned.
 */
export async function convertImageToWebp(input, options = {}) {
  const {
    quality = WEBP_QUALITY,
    maxWidth = 2560,
    maxHeight = 2560,
    maxSizeKB = 0,
  } = options;

  if (!input) return input;

  const originalName = input.name || "image";
  const type = String(input.type || "").toLowerCase();
  if (type && !type.startsWith("image/")) return input;

  if (isWebpFile(input) && !(maxSizeKB > 0)) {
    if (input instanceof File && String(input.name || "").toLowerCase().endsWith(".webp")) {
      return input;
    }
    return asFile(input, toWebpFileName(originalName), WEBP_MIME);
  }

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return input;

    let drawSource;
    let sourceWidth;
    let sourceHeight;

    if (typeof createImageBitmap === "function") {
      drawSource = await createImageBitmap(input);
      sourceWidth = drawSource.width;
      sourceHeight = drawSource.height;
    } else {
      drawSource = await loadImageElement(input);
      sourceWidth = drawSource.naturalWidth || drawSource.width;
      sourceHeight = drawSource.naturalHeight || drawSource.height;
    }

    const { width, height } = scaleSize(sourceWidth, sourceHeight, maxWidth, maxHeight);
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(drawSource, 0, 0, width, height);
    if (typeof drawSource.close === "function") drawSource.close();

    let q = quality;
    let blob = await canvasToBlob(canvas, WEBP_MIME, q);
    if (!blob || blob.type !== WEBP_MIME) return input;

    if (maxSizeKB > 0) {
      while (blob.size / 1024 > maxSizeKB && q > 0.4) {
        q = Math.max(0.4, Number((q - 0.12).toFixed(2)));
        blob = await canvasToBlob(canvas, WEBP_MIME, q);
        if (!blob || blob.type !== WEBP_MIME) break;
      }
    }

    return asFile(blob, toWebpFileName(originalName), WEBP_MIME);
  } catch (err) {
    console.warn("WebP conversion failed, using original file:", err);
    return input;
  }
}
